import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ExternalLink, Loader2, RefreshCw, Save, Send, Plus } from "lucide-react";
import { LOCALE_STATUS_STYLES, PARENT_STATUS_STYLES } from "./ContentParents";
import HeroImagesPanel from "@/components/HeroImagesPanel";

const LOCALE_STATUS_LABELS = {
  NOT_GENERATED: "non générée", GENERATING: "génération…", DRAFT: "brouillon",
  READY_TO_PUBLISH: "prête", PUBLISHING: "publication…", PUBLISHED: "publiée",
  MANUAL_REQUIRED: "publication manuelle", ERROR: "erreur",
};

export default function ContentParentDetail() {
  const { id } = useParams();
  const [parent, setParent] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [manualInfo, setManualInfo] = useState({}); // par locale — survit aux reloads
  const [section, setSection] = useState("content"); // "content" | "images" (Phase E.1)
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/content-parents/${id}`);
      setParent(data);
      const locales = data.locales || [];
      setActive((cur) => cur && locales.some((l) => l.locale === cur) ? cur : locales[0]?.locale || null);
      const generating = locales.some((l) => ["GENERATING", "PUBLISHING"].includes(l.status));
      clearTimeout(pollRef.current);
      if (generating) pollRef.current = setTimeout(load, 3000);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contenu introuvable");
    }
  }, [id]);

  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [load]);

  const loc = parent?.locales?.find((l) => l.locale === active) || null;

  useEffect(() => {
    if (loc && !["GENERATING", "PUBLISHING"].includes(loc.status)) {
      setForm({
        seo_title: loc.seo_title || "", meta_description: loc.meta_description || "",
        slug: loc.slug || "", h1: loc.h1 || "", content_markdown: loc.content_markdown || "",
        og_title: loc.og_title || "", og_description: loc.og_description || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loc?.updated_at]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/content-parents/${id}/locales/${active}`, form);
      toast.success(`Variante ${active} enregistrée — les autres langues ne sont pas modifiées`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setBusy(false); }
  };

  const regenerate = async (locale) => {
    setBusy(true);
    setManualInfo((m) => ({ ...m, [locale]: null }));
    try {
      await api.post(`/content-parents/${id}/generate`, { locales: [locale] });
      toast.info(`Régénération de ${locale} lancée — les autres langues restent intactes`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Régénération impossible");
    } finally { setBusy(false); }
  };

  const publish = async (locale) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/content-parents/${id}/locales/${locale}/publish`);
      if (data.status === "MANUAL_REQUIRED") {
        setManualInfo((m) => ({ ...m, [locale]: data }));
        toast.warning(data.message);
      } else {
        setManualInfo((m) => ({ ...m, [locale]: null }));
        toast.success(`Variante ${locale} publiée : ${data.published_url}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible");
      load();
    } finally { setBusy(false); }
  };

  const addLanguage = async (locale) => {
    setBusy(true);
    try {
      await api.post(`/content-parents/${id}/generate`, { locales: [locale] });
      toast.info(`Génération de ${locale} lancée — les variantes existantes ne sont pas recréées`);
      setActive(locale);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible");
    } finally { setBusy(false); }
  };

  if (!parent) return <div className="p-8 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  const missingLangs = (parent.site_available_languages || []).filter(
    (l) => !(parent.locales || []).some((x) => x.locale === l));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const editable = loc && !["GENERATING", "PUBLISHING"].includes(loc.status);

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="ml-parent-detail">
      <Link to="/content-parents" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#002FA7] mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Contenus multilingues
      </Link>
      <PageHeader
        overline="Contenu multilingue"
        title={parent.topic}
        description={`Zone : ${parent.target_zone || "—"} · Type : ${parent.content_type} · Version génération : ${parent.generation_version}`}
      />
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${PARENT_STATUS_STYLES[parent.status] || "bg-slate-100"}`} data-testid="ml-detail-parent-status">
          Statut parent : {parent.status}
        </span>
        <span className="text-xs text-slate-500">
          Langue principale du site : <b>{parent.site_default_language}</b>
        </span>
      </div>

      {/* Sections : Contenu SEO / Images (E.1) — les contrôles image ne sont jamais
          mélangés au contenu texte */}
      <div className="inline-flex items-center gap-1 mb-4 bg-slate-100 rounded-lg p-1" data-testid="e1-section-switch">
        <button onClick={() => setSection("content")} data-testid="e1-section-content"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${section === "content" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
          Contenu SEO
        </button>
        <button onClick={() => setSection("images")} data-testid="e1-section-images"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${section === "images" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
          Images
        </button>
      </div>

      {section === "images" ? (
        <HeroImagesPanel parentId={id} locales={parent.locales || []}
          defaultLanguage={parent.site_default_language} onLocalesChanged={load} />
      ) : (
      <>
      {/* Onglets par langue */}
      <div className="flex items-center gap-1.5 border-b border-slate-200 mb-4 flex-wrap" data-testid="ml-locale-tabs">
        {(parent.locales || []).map((l) => (
          <button key={l.locale} onClick={() => setActive(l.locale)} data-testid={`ml-tab-${l.locale}`}
            className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${active === l.locale ? "border-[#002FA7] text-[#002FA7] bg-blue-50/50" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <span className="font-mono uppercase">{l.locale}</span>
            <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${LOCALE_STATUS_STYLES[l.status] || ""}`} data-testid={`ml-tab-status-${l.locale}`}>
              {LOCALE_STATUS_LABELS[l.status] || l.status}
            </span>
            {l.enabled === false && <span className="ml-1 text-[9px] text-slate-400" title="Langue retirée de la configuration du site — contenu conservé">retirée</span>}
          </button>
        ))}
        {missingLangs.map((l) => (
          <button key={l} onClick={() => addLanguage(l)} disabled={busy} data-testid={`ml-add-lang-${l}`}
            className="px-2.5 py-1.5 text-xs text-slate-500 border border-dashed border-slate-300 rounded-md hover:border-[#002FA7] hover:text-[#002FA7] inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> {l}
          </button>
        ))}
      </div>

      {!loc ? (
        <div className="text-sm text-slate-500 p-6">Aucune variante.</div>
      ) : ["GENERATING", "PUBLISHING"].includes(loc.status) ? (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-8 text-center" data-testid="ml-locale-generating">
          <Loader2 className="w-6 h-6 animate-spin text-[#002FA7] mx-auto mb-2" />
          <p className="text-sm text-slate-700">{loc.status === "GENERATING" ? `Génération de la variante ${active} en cours…` : "Publication en cours…"}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {loc.status === "ERROR" && (
            <div className="border border-red-200 bg-red-50 rounded-md p-4 flex items-start gap-3" data-testid="ml-locale-error">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-red-800">Échec de génération — {active}</div>
                <div className="text-xs text-red-700 mt-0.5">{loc.error}</div>
              </div>
              <button onClick={() => regenerate(active)} disabled={busy} data-testid="ml-retry-btn"
                className="text-xs font-semibold text-red-700 border border-red-300 rounded-md px-3 py-1.5 hover:bg-red-100 inline-flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Réessayer
              </button>
            </div>
          )}
          {loc.status === "NOT_GENERATED" ? (
            <div className="border border-dashed border-slate-300 bg-white rounded-md p-8 text-center" data-testid="ml-locale-not-generated">
              <p className="text-sm text-slate-600 mb-3">La variante <b className="uppercase">{active}</b> n&apos;a pas encore été générée.</p>
              <button onClick={() => regenerate(active)} disabled={busy}
                data-testid="ml-generate-locale-btn"
                className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#001D6B]">
                <RefreshCw className="w-4 h-4" /> Générer {active}
              </button>
            </div>
          ) : (
            <>
              {loc.published_url && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="ml-published-url">
                  Publiée : <a href={loc.published_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">{loc.published_url} <ExternalLink className="w-3 h-3" /></a>
                </div>
              )}
              {(loc.warnings || []).length > 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="ml-locale-warnings">
                  {loc.warnings.join(" · ")}
                </div>
              )}
              {manualInfo[active] && (
                <div className="border border-orange-200 bg-orange-50 rounded-md p-4 text-xs text-orange-800 space-y-1" data-testid="ml-manual-required">
                  <div className="font-semibold">Publication manuelle requise ({manualInfo[active].capabilities?.provider})</div>
                  <div>{manualInfo[active].message}</div>
                  <div className="font-mono bg-white/70 rounded p-2 mt-1">
                    slug : {manualInfo[active].proposed?.slug} · title : {manualInfo[active].proposed?.seo_title}
                  </div>
                  {manualInfo[active].proposed?.image && (
                    <div className="font-mono bg-white/70 rounded p-2 mt-1" data-testid="ml-manual-image">
                      image — ALT ({active}) : {manualInfo[active].proposed.image.alt ?? "— (non renseigné)"}
                      <span className="block not-italic font-sans mt-0.5">{manualInfo[active].proposed.image.instruction}</span>
                    </div>
                  )}
                  <div className="italic">{manualInfo[active].proposed?.canonical_rule}</div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={`Titre SEO (${active})`} testId="ml-field-seo-title" value={form.seo_title} onChange={set("seo_title")} disabled={!editable} />
                <Field label={`Slug (${active})`} testId="ml-field-slug" value={form.slug} onChange={set("slug")} disabled={!editable} mono />
              </div>
              <Field label={`H1 (${active})`} testId="ml-field-h1" value={form.h1} onChange={set("h1")} disabled={!editable} />
              <Field label={`Méta description (${active})`} testId="ml-field-meta" value={form.meta_description} onChange={set("meta_description")} disabled={!editable} textarea rows={2} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Open Graph — titre" testId="ml-field-og-title" value={form.og_title} onChange={set("og_title")} disabled={!editable} />
                <Field label="Open Graph — description" testId="ml-field-og-desc" value={form.og_description} onChange={set("og_description")} disabled={!editable} />
              </div>
              {(loc.localized_secondary_keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="ml-localized-keywords">
                  <span className="text-[10px] text-slate-500 mr-1">Mots-clés localisés (proposition linguistique) :</span>
                  {loc.localized_secondary_keywords.map((k) => (
                    <span key={k} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{k}</span>
                  ))}
                </div>
              )}
              <Field label={`Contenu markdown (${active})`} testId="ml-field-content" value={form.content_markdown} onChange={set("content_markdown")} disabled={!editable} textarea rows={16} mono />
              {(loc.faq || []).length > 0 && (
                <div className="border border-slate-200 rounded-md p-4" data-testid="ml-locale-faq">
                  <div className="text-xs font-semibold text-slate-700 mb-2">FAQ ({loc.faq.length})</div>
                  {loc.faq.map((f, i) => (
                    <details key={i} className="text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0">
                      <summary className="font-medium cursor-pointer">{f.question}</summary>
                      <p className="mt-1 text-slate-500">{f.answer}</p>
                    </details>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button onClick={save} disabled={busy || !editable} data-testid="ml-save-locale-btn"
                  className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#001D6B] disabled:opacity-50">
                  <Save className="w-4 h-4" /> Enregistrer {active}
                </button>
                <button onClick={() => regenerate(active)} disabled={busy} data-testid="ml-regenerate-btn"
                  className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                  <RefreshCw className="w-4 h-4" /> Régénérer {active}
                </button>
                <button onClick={() => publish(active)} disabled={busy} data-testid="ml-publish-btn"
                  className="inline-flex items-center gap-2 border border-emerald-300 text-emerald-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-50 disabled:opacity-50">
                  <Send className="w-4 h-4" /> Publier {active}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function Field({ label, value, onChange, disabled, textarea, rows = 3, mono, testId }) {
  const cls = `w-full border border-slate-300 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 disabled:bg-slate-50 ${mono ? "font-mono text-xs" : ""}`;
  return (
    <div>
      <label className="text-xs font-medium text-slate-700 mb-1.5 block">{label}</label>
      {textarea
        ? <textarea data-testid={testId} rows={rows} value={value || ""} onChange={onChange} disabled={disabled} className={cls} />
        : <input data-testid={testId} value={value || ""} onChange={onChange} disabled={disabled} className={cls} />}
    </div>
  );
}
